import { useMutation } from '@base-app/web2'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_APPLICATION, UPDATE_APPLICATION } from '../../../graphql/applications/mutations'
import { APP_QUERY } from '../../../graphql/applications/queries'
import type { Application } from '../../../graphql/applications/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import {
  APPLICATION_TABS,
  type ApplicationTabId,
  DEFAULT_APPLICATION_TAB,
  FIELD_TAB,
} from '../../../lib/applicationTabs'
import { prependToLists } from '../../../lib/cacheLists'
import { focusFieldById } from '../../../lib/richText'
import {
  compareVersions,
  extractVersionGateError,
  isValidAndroidStoreUrl,
  isValidBaseUrl,
  isValidIosStoreUrl,
  isValidVersionFormat,
  normalizeBaseUrl,
} from '../../../lib/versionGate'
import { Button, FormActions } from '../../atoms'
import { FormLoadState, type TabItem, Tabs } from '../../molecules'
import {
  type ApplicationErrors,
  type ApplicationValues,
  type ErrorField,
  FIELD_IDS,
  valuesFromApplication,
} from './applicationFormTypes'
import { ApplicationAccessesTab } from './tabs/ApplicationAccessesTab'
import { ApplicationGeneralTab } from './tabs/ApplicationGeneralTab'
import { ApplicationNotificationsTab } from './tabs/ApplicationNotificationsTab'
import { ApplicationVersionTab } from './tabs/ApplicationVersionTab'

interface ApplicationFormFieldsProps {
  application?: Application
  clientId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
  // The open tab, when the page keeps it in the URL; without it the form keeps its own
  activeTab?: ApplicationTabId
  onTabChange?: (tab: ApplicationTabId) => void
  // Tells the page whether the fields differ from the saved record (to warn before leaving)
  onDirtyChange?: (dirty: boolean) => void
}

// The organism of each tab: adding a tab adds a line here and one in APPLICATION_TABS
const TAB_ORGANISMS = {
  general: ApplicationGeneralTab,
  accesses: ApplicationAccessesTab,
  version: ApplicationVersionTab,
  notifications: ApplicationNotificationsTab,
}

export const ApplicationFormFields = memo(
  ({
    application,
    clientId,
    onSuccess,
    onClose,
    skeleton,
    activeTab,
    onTabChange,
    onDirtyChange,
  }: ApplicationFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const isEdit = !!application
    const initial = useMemo(() => valuesFromApplication(application), [application])
    const [values, setValues] = useState<ApplicationValues>(initial)
    const [errors, setErrors] = useState<ApplicationErrors>({})
    const [ownTab, setOwnTab] = useState<ApplicationTabId>(DEFAULT_APPLICATION_TAB)
    const tab = activeTab ?? ownTab
    const changeTab = onTabChange ?? setOwnTab

    const {
      name,
      onesignalId,
      onesignalKey,
      livestream,
      accesses,
      observations,
      minVersion,
      latestVersion,
      storeUrlAndroid,
      storeUrlIos,
      apiBaseUrl,
      imageBaseUrl,
    } = values

    const setValue = useCallback(
      <K extends keyof ApplicationValues>(key: K, value: ApplicationValues[K]) => {
        setValues(prev => ({ ...prev, [key]: value }))
      },
      [],
    )

    const dirty = useMemo(
      () => JSON.stringify(values) !== JSON.stringify(initial),
      [values, initial],
    )
    useEffect(() => {
      onDirtyChange?.(dirty)
    }, [dirty, onDirtyChange])

    // Opens the tab that holds the field, then moves focus into it once the panel is visible
    const revealAndFocus = useCallback(
      (field: ErrorField) => {
        changeTab(FIELD_TAB[field])
        focusFieldById(FIELD_IDS[field])
      },
      [changeTab],
    )

    const makeOptimisticApp = useCallback(
      (id: string): Application => ({
        __typename: 'Application',
        id,
        client_id: clientId,
        name,
        onesignal_id: onesignalId || null,
        onesignal_rest_api_key: onesignalKey || null,
        livestream_preset: livestream || null,
        accesses,
        // Viewer-specific: the API recomputes them from the saved accesses; until then keep the known ones
        viewerPermissions: application?.viewerPermissions ?? [],
        viewerCanManageUsers: application?.viewerCanManageUsers ?? false,
        observations: observations || null,
        min_version: minVersion.trim() || null,
        latest_version: latestVersion.trim() || null,
        store_url_android: storeUrlAndroid.trim() || null,
        store_url_ios: storeUrlIos.trim() || null,
        api_base_url: normalizeBaseUrl(apiBaseUrl) || null,
        image_base_url: normalizeBaseUrl(imageBaseUrl) || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [
        clientId,
        application,
        name,
        onesignalId,
        onesignalKey,
        livestream,
        accesses,
        observations,
        minVersion,
        latestVersion,
        storeUrlAndroid,
        storeUrlIos,
        apiBaseUrl,
        imageBaseUrl,
      ],
    )

    const handleError = useCallback(
      (err: Error) => {
        const vgError = extractVersionGateError(err)
        if (vgError) {
          const { reason, field } = vgError
          if (reason === 'INVALID_VERSION_FORMAT' || reason === 'VERSION_TOO_LONG') {
            const msg =
              reason === 'VERSION_TOO_LONG'
                ? t('applications.form.versionTooLong')
                : t('applications.form.versionFormatError')
            if (field === 'latest_version') {
              setErrors(prev => ({ ...prev, latestVersion: msg }))
              revealAndFocus('latestVersion')
            } else {
              setErrors(prev => ({ ...prev, minVersion: msg }))
              revealAndFocus('minVersion')
            }
          } else if (reason === 'VERSION_ORDER_INVALID') {
            setErrors(prev => ({ ...prev, minVersion: t('applications.form.versionOrderError') }))
            revealAndFocus('minVersion')
          } else if (reason === 'STORE_URL_REQUIRED') {
            // Only the empty one(s): a filled store URL isn't the problem
            const missing = {
              ...(storeUrlAndroid.trim() ? {} : { storeUrlAndroid: true }),
              ...(storeUrlIos.trim() ? {} : { storeUrlIos: true }),
            }
            const marked = Object.keys(missing).length
              ? missing
              : { storeUrlAndroid: true, storeUrlIos: true }
            setErrors(prev => ({
              ...prev,
              ...(marked.storeUrlAndroid && {
                storeUrlAndroid: t('applications.form.storeUrlRequired'),
              }),
              ...(marked.storeUrlIos && { storeUrlIos: t('applications.form.storeUrlRequired') }),
            }))
            revealAndFocus(marked.storeUrlAndroid ? 'storeUrlAndroid' : 'storeUrlIos')
          } else if (
            reason === 'INVALID_BASE_URL' ||
            reason === 'INVALID_BASE_URL_HOST' ||
            reason === 'BASE_URL_TOO_LONG'
          ) {
            const msg =
              reason === 'BASE_URL_TOO_LONG'
                ? t('applications.form.baseUrlTooLong')
                : t('applications.form.baseUrlInvalid')
            if (field === 'image_base_url') {
              setErrors(prev => ({ ...prev, imageBaseUrl: msg }))
              revealAndFocus('imageBaseUrl')
            } else {
              setErrors(prev => ({ ...prev, apiBaseUrl: msg }))
              revealAndFocus('apiBaseUrl')
            }
          } else if (
            reason === 'INVALID_STORE_URL' ||
            reason === 'INVALID_STORE_URL_HOST' ||
            reason === 'STORE_URL_TOO_LONG'
          ) {
            if (field === 'store_url_ios') {
              const msg =
                reason === 'STORE_URL_TOO_LONG'
                  ? t('applications.form.storeUrlTooLong')
                  : t('applications.form.storeUrlIosInvalid')
              setErrors(prev => ({ ...prev, storeUrlIos: msg }))
              revealAndFocus('storeUrlIos')
            } else {
              const msg =
                reason === 'STORE_URL_TOO_LONG'
                  ? t('applications.form.storeUrlTooLong')
                  : t('applications.form.storeUrlAndroidInvalid')
              setErrors(prev => ({ ...prev, storeUrlAndroid: msg }))
              revealAndFocus('storeUrlAndroid')
            }
          }
        } else {
          showToast(err.message, 'error')
        }
      },
      [showToast, t, storeUrlAndroid, storeUrlIos, revealAndFocus],
    )

    const [createApp, { loading: creating }] = useMutation(CREATE_APPLICATION, {
      optimisticResponse: { createApplication: makeOptimisticApp(`temp-${Date.now()}`) },
      update(cache, result) {
        const newApp = result.data?.createApplication
        if (!newApp) {
          return
        }
        prependToLists(cache, 'applications', newApp, { clientId })
      },
      onError: handleError,
      onCompleted() {
        onSuccess()
      },
    })

    const [updateApp, { loading: updating }] = useMutation(UPDATE_APPLICATION, {
      optimisticResponse: { updateApplication: makeOptimisticApp(application?.id ?? '') },
      onError: handleError,
      onCompleted() {
        onSuccess()
      },
    })

    const loading = creating || updating

    const validate = useCallback(() => {
      const errs: ApplicationErrors = {}
      if (!name.trim()) {
        errs.name = t('common.form.nameRequired')
      }
      const trimmedMin = minVersion.trim()
      const trimmedLatest = latestVersion.trim()
      const trimmedAndroid = storeUrlAndroid.trim()
      const trimmedIos = storeUrlIos.trim()

      if (trimmedMin && !isValidVersionFormat(trimmedMin)) {
        errs.minVersion = t('applications.form.versionFormatError')
      }
      if (trimmedLatest && !isValidVersionFormat(trimmedLatest)) {
        errs.latestVersion = t('applications.form.versionFormatError')
      }
      if (!errs.minVersion && !errs.latestVersion && trimmedMin && trimmedLatest) {
        if (compareVersions(trimmedMin, trimmedLatest) > 0) {
          errs.minVersion = t('applications.form.versionOrderError')
        }
      }
      if (trimmedMin) {
        if (!trimmedAndroid) {
          errs.storeUrlAndroid = t('applications.form.storeUrlRequired')
        }
        if (!trimmedIos) {
          errs.storeUrlIos = t('applications.form.storeUrlRequired')
        }
      }
      if (trimmedAndroid && !isValidAndroidStoreUrl(trimmedAndroid)) {
        errs.storeUrlAndroid = t('applications.form.storeUrlAndroidInvalid')
      }
      if (trimmedIos && !isValidIosStoreUrl(trimmedIos)) {
        errs.storeUrlIos = t('applications.form.storeUrlIosInvalid')
      }
      const trimmedApi = apiBaseUrl.trim()
      const trimmedImage = imageBaseUrl.trim()
      if (trimmedApi && !isValidBaseUrl(trimmedApi)) {
        errs.apiBaseUrl = t('applications.form.baseUrlInvalid')
      }
      if (trimmedImage && !isValidBaseUrl(trimmedImage)) {
        errs.imageBaseUrl = t('applications.form.baseUrlInvalid')
      }
      setErrors(errs)
      // Move to the first invalid field, in form order, opening its tab
      const firstInvalid = (Object.keys(FIELD_IDS) as ErrorField[]).find(key => errs[key])
      if (firstInvalid) {
        revealAndFocus(firstInvalid)
      }
      return Object.keys(errs).length === 0
    }, [
      name,
      minVersion,
      latestVersion,
      storeUrlAndroid,
      storeUrlIos,
      apiBaseUrl,
      imageBaseUrl,
      revealAndFocus,
      t,
    ])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const vars = {
          name,
          onesignal_id: onesignalId || null,
          onesignal_rest_api_key: onesignalKey || null,
          livestream_preset: livestream || null,
          accesses,
          observations: observations || null,
          min_version: minVersion.trim() || null,
          latest_version: latestVersion.trim() || null,
          store_url_android: storeUrlAndroid.trim() || null,
          store_url_ios: storeUrlIos.trim() || null,
          api_base_url: normalizeBaseUrl(apiBaseUrl) || null,
          image_base_url: normalizeBaseUrl(imageBaseUrl) || null,
        }
        if (isEdit) {
          updateApp({ variables: { id: application.id, ...vars } })
        } else {
          createApp({ variables: { client_id: clientId, ...vars } })
        }
      },
      [
        validate,
        isEdit,
        application?.id,
        name,
        onesignalId,
        onesignalKey,
        livestream,
        accesses,
        observations,
        minVersion,
        latestVersion,
        storeUrlAndroid,
        storeUrlIos,
        apiBaseUrl,
        imageBaseUrl,
        clientId,
        updateApp,
        createApp,
      ],
    )

    const tabs: TabItem[] = APPLICATION_TABS.map(({ id, labelKey }) => {
      const Organism = TAB_ORGANISMS[id]
      return {
        id,
        label: t(labelKey),
        hasError: (Object.keys(errors) as ErrorField[]).some(f => errors[f] && FIELD_TAB[f] === id),
        content: (
          <Organism values={values} errors={errors} onChange={setValue} skeleton={skeleton} />
        ),
      }
    })

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <Tabs
          tabs={tabs}
          activeId={tab}
          onChange={id => changeTab(id as ApplicationTabId)}
          idPrefix="app"
          aria-label={t('applications.page.tabsLabel')}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('applications.form.saving') : t('applications.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

ApplicationFormFields.displayName = 'ApplicationFormFields'

interface ApplicationFormProps extends Omit<ApplicationFormFieldsProps, 'application'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const ApplicationForm = memo(({ id, ...props }: ApplicationFormProps) => {
  const { record, status } = useRecordById(APP_QUERY, id, data => data.application)
  if (status === 'loading') {
    return <ApplicationFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  // An id from another client's URL must not edit that app under this client
  if (record && record.client_id !== props.clientId) {
    return <FormLoadState status="missing" onClose={props.onClose} />
  }
  return <ApplicationFormFields key={id ?? 'new'} application={record} {...props} />
})

ApplicationForm.displayName = 'ApplicationForm'
